import { type SiteId } from '@solinkcorp/api-types';
import { defaultUserPreferences } from '@solinkcorp/authentication';
import { ComponentLoadingSpinner } from '@solinkcorp/solink-components';
import { type IEvent } from 'clients/PlatformApi';
import { withAspen } from 'components/aspen/withAspen';
import Loadable from 'components/Loadable';
import { type AspenCameraDrawerRegistry } from 'components/VideoPlayer/aspen/useAspenCameraDrawer';
import { type VideoPlayerProps } from 'components/VideoPlayer/VideoPlayer';
import React, { type ComponentType } from 'react';
import { useControllerState } from 'solox';
import CameraPlayer from 'src/core/video/components/players/CameraPlayer';
import { type CamerasDictionary, type VideoDiscoverySite } from 'utils/types';
import { useTimelineController } from './components/VideoHistogram/TimelineController';
import { type PlaybackController } from './controller/PlaybackController';
import { useSyncPlayingCameras } from './controller/useSyncPlayingCameras';

const VideoPlayer = Loadable({
  loader: () => import('components/VideoPlayer/VideoPlayer'),
  loading: ComponentLoadingSpinner,
});

const AspenVideoPlayer = Loadable({
  loader: () =>
    import('components/VideoPlayer/aspen/AspenVideoPlayer').then((module) => ({
      default: module.AspenVideoPlayer,
    })),
  loading: ComponentLoadingSpinner,
});

/**
 * The second player entry point, beside `AuthenticatedVideoPlayer`. It serves the
 * alarm verification pages, which are authenticated, and the public share and alarm
 * pages, which are not. A public session has no user features, so the gate resolves
 * to Legacy there.
 */
type GatedPlayerProps = VideoPlayerProps & {
  cameraDrawerRegistry?: AspenCameraDrawerRegistry;
};

const AspenGatedVideoPlayer = withAspen<GatedPlayerProps>(
  VideoPlayer as ComponentType<GatedPlayerProps>,
  AspenVideoPlayer,
);

interface PublicVideoPlayerProps extends Omit<
  VideoPlayerProps,
  | 'preferences'
  | 'timelineController'
  | 'inProgressEvents'
  | 'motionSegmentsByCamera'
  | 'allLocationsAreLocal'
  | 'timeFormat'
  | 'toggleShowKeyboardShortcuts'
  | 'pinnedEvent'
  | 'eventStoreVideoStartTime'
  | 'eventStandard'
  | 'locationsAreLocalById'
  | 'setIsCameraDrawerVisible'
> {
  timezone: string | null;
  filterDate: number;
  playbackController: PlaybackController;
  timelineController?: VideoPlayerProps['timelineController'];
  motionSegmentsByCamera?: VideoPlayerProps['motionSegmentsByCamera'];
  camerasById: CamerasDictionary;
  sitesById:
    | {
        [siteId: SiteId]: VideoDiscoverySite;
      }
    | undefined;
  devicesById?: Record<string, any>;
  isMobile?: boolean;
  timelineEvents?: IEvent[];
  pinnedEvent?: IEvent;
  inProgressEvents: IEvent[];
  cameraDrawerRegistry?: AspenCameraDrawerRegistry;
}

const PublicVideoPlayer: React.FC<PublicVideoPlayerProps> = (props) => {
  const {
    playingCamerasController,
    camerasById,
    sitesById,
    playbackController,
    videoPlayerOptionsController,
    isMobile,
    pinnedEvent,
  } = props;

  const devicesById = React.useMemo(() => props.devicesById ?? {}, [props.devicesById]);
  const playingCameras = useControllerState(playingCamerasController.state, () =>
    playingCamerasController.getPlayingCameras(),
  );

  useSyncPlayingCameras({
    playingCamerasController,
    camerasById,
    sitesById,
    devicesById,
  });

  const desiredTime = useControllerState(playbackController.state, (state) => state.desiredTime);

  const truncateEventsSetting = useControllerState(
    videoPlayerOptionsController.state,
    (state) => state.truncateEvents,
  );

  // Create timeline controller internally if not provided
  const internalTimelineController = useTimelineController({
    playerTime: desiredTime,
    timezone: props.timezone || '',
    hoveredEvent: props.hoveredEvent,
    onHoveredEventChange: props.onHoveredEventChange,
    showAllEvents: props.showAllEvents,
    onShowAllEventsChange: props.onShowAllEventsChange,
    onMoreEventsClicked: props.onMoreEventsClicked,
    truncateEvents: !!truncateEventsSetting,
  });

  // Use provided timelineController or fall back to internal one
  const timelineController = props.timelineController ?? internalTimelineController;

  // Use provided motionSegmentsByCamera or fall back to empty object
  const motionSegmentsByCamera = props.motionSegmentsByCamera ?? {};

  const timeFormat = 'twelvehour';

  const toggleShowKeyboardShortcuts = () => null;

  if (!!isMobile && !!playingCameras.length) {
    return (
      <CameraPlayer
        playbackController={playbackController}
        playingCamera={playingCameras?.[0]}
        locationIsLocal={false}
        isMuted={true}
        nativeControls={true}
      />
    );
  }

  return (
    <AspenGatedVideoPlayer
      {...props}
      preferences={defaultUserPreferences}
      timelineController={timelineController}
      motionSegmentsByCamera={motionSegmentsByCamera}
      allLocationsAreLocal={false}
      timeFormat={timeFormat}
      toggleShowKeyboardShortcuts={toggleShowKeyboardShortcuts}
      pinnedEvent={pinnedEvent}
      eventStoreVideoStartTime={undefined}
      locationsAreLocalById={{}}
    />
  );
};

export default PublicVideoPlayer;
